// External libraries
import { useMemo, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useCIFService } from '../../services';
import { User, loggedUserState, geoJsonState, spinnerState, errorState, geoState, selectedTabState } from '../../states';
import { Drawer, Body, BodyType, BodyColor, Heading, TypographyType, TypographyColor, Input, Button, ButtonTheme, ButtonSize, ButtonVariant } from '../../ui-elements';
import { getParentGeoId, useHandleError } from '../../helpers';


interface RequestDataProps {
    isOpen: boolean,
    setIsOpen: (isDrawerOpen: boolean) => void;
    geoName: any;
    subHeading: string;
    defaultText?: string
    purpose: string;
    requestedRegionForPoi?: any;
}

export function RequestData({ isOpen, setIsOpen, geoName, subHeading, defaultText, purpose, requestedRegionForPoi }: RequestDataProps) {
    const loggedUser = useRecoilValue<User>(loggedUserState);
    const { rootProperties } = useRecoilValue(geoJsonState);
    const selectedGeo = useRecoilValue(geoState);
    const selectedTab = useRecoilValue(selectedTabState);
    const { getErrorMsg } = useHandleError();
    const cifService = useCIFService();
    const setSpinner = useSetRecoilState(spinnerState);
    const setError = useSetRecoilState(errorState);
    const [message, setMessage] = useState<string>('');

    const geoCode = useMemo(() => {
        const { country, state, district, sub_district, village } = selectedGeo;
        const geo_id = village || sub_district || district || state || country;
        return Number(geo_id);
    }, [selectedGeo]);

    const handleChangeData = (e: any) => {
        e.preventDefault();
        const value = e.target.value;

        const wordLimit = 250;
        const words = value.split(/\s+/).filter((word: any) => word !== '');

        if (words.length > wordLimit) {
            // Truncate the input to the word limit
            const truncatedInput = words.slice(0, wordLimit).join(' ');
            setMessage(truncatedInput);
        }
        else {
            setMessage(value);
        }
    };

    const handleSendClick = () => {
        if (message) {
            const { parent, type } = getParentGeoId(Number(rootProperties?.geoId), selectedGeo, selectedTab);
            // setSpinner(prev => ({ ...prev, spinners: [...prev.spinners, 'sendEmail'] }));
            const payload = {
                email_id: loggedUser?.email_id,
                name: loggedUser?.name,
                company: loggedUser?.company_name,
                message,
                geo_code: requestedRegionForPoi?.geoId || rootProperties?.geoId,
                geo_name: requestedRegionForPoi?.geoName || rootProperties?.geoName,
                purpose,
                parentgeoId: parent,
                type,
            };
            setIsOpen(false);
            cifService.sendEmail(payload).then((response: any) => {
                
                setError({ type: purpose === 'PoI data' ? 'Data Requested' : 'Success', message: response.detail });
                setMessage('');
            }).catch((error: any) => {
                getErrorMsg(error);
            }).finally(() => {
                // setSpinner(prev => ({ ...prev, spinners: prev.spinners.filter(s => s !== 'sendEmail') }));
                setMessage('');
                // setIsOpen(false);
            });
        }
        else {
            setError({ type: 'Error', message: 'Write something!' });
        }
    };

    return (
        <Drawer
            id='request-data'
            title={`Request Data (${geoName})`}
            isOpen={isOpen}
            toggleFunction={() => setIsOpen(false)}
            style={{ overflowY: 'auto', overflowX: 'hidden' }}
        >
            <div className='d-flex flex-column align-items-start justify-content-center text-start'>
                <Body
                    type={BodyType.p2}
                    color={BodyColor.muted}
                    classname='margin-bottom-2'
                >
                    {subHeading}
                </Body>

                <Heading
                    title='Name*'
                    type={TypographyType.h5}
                    colour={TypographyColor.primary}
                    classname='margin-top-3'
                />
                <Input
                    type="text"
                    placeholder="Enter your name"
                    value={loggedUser.name || ''}
                    disabled={true}
                />
                <Heading
                    title='Email*'
                    type={TypographyType.h5}
                    colour={TypographyColor.primary}
                    classname='margin-top-3'
                />
                <Input
                    type="email"
                    placeholder="Enter your Email ID"
                    value={loggedUser.email_id || ''}
                    disabled={true}
                />
                <Heading
                    title='Message*'
                    type={TypographyType.h5}
                    colour={TypographyColor.primary}
                    classname='margin-top-3'
                />
                <textarea
                    value={message}
                    name='message'
                    onChange={(e) => handleChangeData(e)}
                    placeholder={defaultText ? defaultText : "Type your request message (Max 250 words)"}
                    style={{ height: '10rem' }}
                    className='fs-13 padding-3 rounded w-100'
                />
                <Button
                    theme={ButtonTheme.primary}
                    size={ButtonSize.large}
                    variant={ButtonVariant.bordered}
                    onClick={() => handleSendClick()}
                    classname='margin-top-bottom-3 height-3'
                >
                    Send Message
                </Button>
            </div>
        </Drawer>
    );
}